"use client";

import Image from "next/image";
import { Link, usePathname } from "@/i18n/navigation";
import { InstagramIcon, LinkedInIcon, TikTokIcon } from "@/components/icons/BrandIcons";
import { useLocale, useTranslations } from "next-intl";
import { NewsletterForm } from "@/components/shared/ui/NewsletterForm";
import { marketing_footer_groups, type MarketingNavLink } from "@/components/shared/Navbar/marketing-nav";

function FooterLink({ item }: { item: MarketingNavLink }) {
	const className = "text-base font-normal text-white transition duration-150 ease-in-out hover:text-white/80";

	if (item.external) {
		return (
			<a href={item.href} target="_blank" rel="noopener noreferrer" className={className}>
				{item.title}
			</a>
		);
	}

	return (
		<Link href={item.href} className={className}>
			{item.title}
		</Link>
	);
}

export default function Footer() {
	const pathname = usePathname();
	const t = useTranslations();
	const locale = useLocale();

	if (pathname.includes("/book-a-demo")) return <></>;

	return (
		<footer className="relative flex w-full flex-col overflow-hidden bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue">
			<div className="relative z-10 px-8 py-10 text-white sm:px-12 sm:py-20 lg:px-20">
				<div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
					<div className="col-span-1 flex flex-col items-center lg:col-span-4 lg:items-start">
						<div className="flex w-full flex-col items-center sm:hidden">
							<div className="mb-6 flex items-center justify-center space-x-4">
								<Link href="/">
									<Image
										src="/StudyStash_Logo_White.png"
										alt="StudyStash Logo"
										width={120}
										height={34}
										className="h-auto cursor-pointer object-contain"
									/>
								</Link>
								<div className="flex space-x-2">
									<SocialLinks />
								</div>
							</div>
							<div className="flex w-full max-w-sm justify-center pb-4">
								<NewsletterForm />
							</div>
						</div>

						<div className="hidden w-full flex-col items-start sm:flex">
							<div className="flex w-full items-start justify-between lg:flex-col">
								<div className="flex flex-col">
									<Link href="/" className="mb-4">
										<Image
											src="/StudyStash_Logo_White.png"
											alt="StudyStash Logo"
											width={208}
											height={58}
											className="h-auto cursor-pointer object-contain"
										/>
									</Link>
									<div className="flex space-x-2">
										<SocialLinks />
									</div>
								</div>
								<div className="w-full pl-8 pt-0 lg:pl-0 lg:pt-8">
									<NewsletterForm />
								</div>
							</div>
						</div>
					</div>

					<div className="col-span-1 grid grid-cols-2 gap-x-8 gap-y-6 sm:grid-cols-4 lg:col-span-8 lg:gap-4">
						{marketing_footer_groups.map((group) => (
							<div key={group.title} className="flex flex-col">
								<h5 className="pb-2 text-lg font-semibold text-white sm:mb-2">{group.title}</h5>
								<ul className="flex list-none flex-col gap-1">
									{group.items.map((item) => (
										<li key={item.href} className="list-none">
											<FooterLink item={item} />
										</li>
									))}
								</ul>
							</div>
						))}
					</div>
				</div>
			</div>

			<div className="relative z-10 flex w-full flex-col items-center justify-between border-t border-white/20 bg-white px-8 pb-12 pt-8 text-white sm:px-12 sm:py-12 lg:flex-row lg:px-20">
				<div className="flex w-full flex-col items-center gap-4 text-gray-800 lg:w-auto lg:items-start lg:gap-2">
					<div className="flex flex-wrap justify-center gap-4 text-sm lg:justify-start">
						<Link href="/privacy-policy" className="text-sm underline transition-colors hover:text-gray-600 lg:text-xs">
							Privacy Policy
						</Link>
						<Link href="/terms-of-use" className="text-sm underline transition-colors hover:text-gray-600 lg:text-xs">
							Terms of Use
						</Link>
						<Link href="/accessibility-statement" className="text-sm underline transition-colors hover:text-gray-600 lg:text-xs">
							Accessibility Statement
						</Link>
						<Link href="/trustworthy-and-responsible-ai" className="text-sm underline transition-colors hover:text-gray-600 lg:text-xs">
							Trustworthy and Responsible AI
						</Link>
					</div>
					<div className="space-y-1 text-center text-sm lg:text-left lg:text-xs">
						<span>&copy; Copyright {t("general.entity")} 2026, All rights reserved.</span>
						<div>{t("general.address")}</div>
					</div>
				</div>

				<div className="mt-8 flex w-full flex-wrap items-center justify-center gap-6 lg:mt-0 lg:w-auto lg:justify-end lg:py-2">
					<Image src="/EAB.png" alt="EAB Partner" width={72} height={40} className="h-8 w-auto xl:h-10" />
					<Image src="/educause.png" alt="EDUCAUSE Member" width={110} height={40} className="h-8 w-auto xl:h-10" />
					{locale !== "en-us" && (
						<Image src="/ALT-logo.png" alt="Association for Learning Technology" width={92} height={64} className="h-12 w-auto xl:h-16" />
					)}
					{locale === "en-us" ? (
						<>
							<Image src="/COPPA.png" alt="COPPA Compliant" width={64} height={64} className="h-12 w-auto xl:h-16" />
							<Image src="/FERPA.png" alt="FERPA Compliant" width={64} height={64} className="h-12 w-auto xl:h-16" />
							<Image src="/CCPA.png" alt="CCPA Compliant" width={64} height={64} className="h-12 w-auto xl:h-16" />
							<Image src="/ISO_IEC 27001_2022.png" alt="ISO 27001 Certified" width={64} height={64} className="h-12 w-auto xl:h-16" />
							<Image src="/508.png" alt="Section 508 Compliant" width={64} height={64} className="h-12 w-auto xl:h-16" />
						</>
					) : (
						<>
							<Image src="/ISO_IEC 27001_2022.png" alt="ISO 27001 Certified" width={64} height={64} className="h-12 w-auto lg:h-10 xl:h-14" />
							<Image src="/gdpr_badge.png" alt="GDPR Compliant" width={64} height={64} className="h-12 w-auto xl:h-16" />
						</>
					)}
				</div>
			</div>
		</footer>
	);
}

function SocialLinks() {
	return (
		<>
			<Link
				href="https://www.linkedin.com/company/studystash/"
				className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:scale-105 hover:bg-white/20"
				aria-label="Follow StudyStash on LinkedIn"
			>
				<LinkedInIcon className="h-6 w-6 lg:h-7 lg:w-7" />
			</Link>
			<Link
				href="https://www.instagram.com/studystash_app/"
				className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:scale-105 hover:bg-white/20"
				aria-label="Follow StudyStash on Instagram"
			>
				<InstagramIcon className="h-6 w-6 lg:h-7 lg:w-7" />
			</Link>
			<Link
				href="https://www.tiktok.com/@studystash?_t=8eJSsXD5NqU&_r=1"
				className="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-lg text-white transition-all duration-200 hover:scale-105 hover:bg-white/20"
				aria-label="Follow StudyStash on TikTok"
			>
				<TikTokIcon className="h-6 w-6 lg:h-7 lg:w-7" />
			</Link>
		</>
	);
}
